import React, { useState, useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
  ChevronRightIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { apiRequest } from '../../utils/apiUtils';
import { useInvalidateUnreadNotificationCount } from '../../hooks/useUnreadNotificationCount';
// import { formatDistanceToNow } from 'date-fns';
import LoadingSpinner from './LoadingSpinner';

const NotificationCenter = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { showNotification, updateUnreadCount } = useUI();
  const invalidateUnreadCount = useInvalidateUnreadNotificationCount();
  const { connections } = useConnection();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const dropdownRef = useRef(null);

  // Load notifications when component mounts or opens
  useEffect(() => {
    if (isOpen && user) {
      loadNotifications();
    }
  }, [isOpen, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/notifications?limit=20');

      if (response.success) {
        setNotifications(response.notifications || []);
        setUnreadCount(response.unread_count || 0);
        // Update global badge count when loading notifications
        updateUnreadCount(response.unread_count || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Don't show error notification here as it would be annoying
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await apiRequest(`/notifications/${notificationId}/read`, {
        method: 'POST'
      });

      if (response.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Update global badge count optimistically + invalidate the
        // shared React Query cache so Header doesn't push a stale value.
        updateUnreadCount(Math.max(0, unreadCount - 1));
        invalidateUnreadCount();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showNotification('Failed to mark notification as read', 'error');
    }
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      const response = await apiRequest('/notifications/read-all', {
        method: 'POST'
      });

      if (response.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(notification => ({
            ...notification,
            read: true,
            read_at: new Date().toISOString()
          }))
        );
        setUnreadCount(0);
        // Update global badge count optimistically + invalidate cache
        updateUnreadCount(0);
        invalidateUnreadCount();
        showNotification('All notifications marked as read', 'success');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showNotification('Failed to mark all notifications as read', 'error');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const getNotificationIcon = (type, severity) => {
    const iconClass = "h-5 w-5";

    switch (type) {
      case 'mention':
        return <ChatBubbleLeftIcon className={`${iconClass} text-primary-600`} />;
      case 'validation_failure':
      case 'automation_failure':
        return <ExclamationTriangleIcon className={`${iconClass} text-danger-500`} />;
      case 'issue_created':
      case 'issue_updated':
        return <ExclamationTriangleIcon className={`${iconClass} text-warning-500`} />;
      case 'schema_change':
        return <InformationCircleIcon className={`${iconClass} text-primary-500`} />;
      case 'automation_completion':
        return <CheckIcon className={`${iconClass} text-accent-500`} />;
      default:
        return <BellIcon className={`${iconClass} text-secondary-500`} />;
    }
  };

  const getNotificationColor = (type, severity) => {
    if (severity === 'high') return 'border-l-danger-500 bg-danger-50';
    if (severity === 'medium') return 'border-l-warning-500 bg-warning-50';
    if (severity === 'low') return 'border-l-primary-500 bg-primary-50';
    
    switch (type) {
      case 'validation_failure':
      case 'automation_failure':
        return 'border-l-danger-500 bg-danger-50';
      case 'issue_created':
      case 'issue_updated':
        return 'border-l-warning-500 bg-warning-50';
      case 'schema_change':
        return 'border-l-primary-500 bg-primary-50';
      case 'automation_completion':
        return 'border-l-accent-500 bg-accent-50';
      default:
        return 'border-l-secondary-500 bg-secondary-50';
    }
  };

  const formatNotificationTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      
      return date.toLocaleDateString();
    } catch (error) {
      return 'Unknown time';
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if not already read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Handle navigation based on notification type and metadata
    const { metadata } = notification;
    
    switch (notification.type) {
      case 'mention':
        if (metadata.issue_id) {
          navigate(`/issues/${metadata.issue_id}`);
        } else {
          navigate('/issues');
        }
        break;
      case 'validation_failure':
        if (metadata.table_name) {
          // Navigate to validation page for that table
          navigate(`/validations?table=${encodeURIComponent(metadata.table_name)}`);
        } else {
          navigate('/validations');
        }
        break;
      case 'issue_created':
      case 'issue_updated':
        if (metadata.issue_id) {
          navigate(`/issues/${metadata.issue_id}`);
        } else {
          navigate('/issues');
        }
        break;
      case 'schema_change':
        if (metadata.table_name) {
          navigate(`/data-explorer/${encodeURIComponent(metadata.table_name)}`);
        } else {
          navigate('/analytics/schema-changes');
        }
        break;
      case 'automation':
        // Handle automation notifications based on their content
        if (notification.title && notification.title.includes('Validation Failures')) {
          // For validation failure notifications, go to validations page
          if (metadata.table_name) {
            navigate(`/validations?table=${encodeURIComponent(metadata.table_name)}`);
          } else {
            navigate('/validations');
          }
        } else if (notification.title && notification.title.includes('Schema Changes')) {
          // For schema change notifications, go to schema changes page
          if (metadata.table_name) {
            navigate(`/analytics/schema-changes/${metadata.connection_id}`);
          } else {
            navigate('/analytics/schema-changes');
          }
        } else if (notification.title && notification.title.includes('Job Failed')) {
          // For automation job failures, go to automation page
          navigate('/automation');
        } else {
          // For other automation notifications, go to relevant page based on metadata
          if (metadata.connection_id) {
            // Look up connection to get its slug for human-readable URL
            const conn = connections.find(c => c.id === metadata.connection_id);
            navigate(`/connections/${conn?.slug || metadata.connection_id}`);
          } else {
            navigate('/dashboard');
          }
        }
        break;
      default:
        // For unhandled notification types, close the notification center
        break;
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Transition
      show={isOpen}
      enter="transition ease-out duration-200"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-150"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <div 
        ref={dropdownRef}
        className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50 max-h-96 overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-secondary-200 bg-secondary-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-secondary-900 flex items-center">
              <BellIcon className="h-4 w-4 mr-2" />
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {unreadCount} new
                </span>
              )}
            </h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={markingAllRead}
                  className="text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50"
                >
                  {markingAllRead ? 'Marking...' : 'Mark all read'}
                </button>
              )}
              <button
                onClick={onClose}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-72 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="sm" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <BellIcon className="h-8 w-8 text-secondary-300 mx-auto mb-3" />
              <p className="text-sm text-secondary-500">No notifications yet</p>
              <p className="text-xs text-secondary-400 mt-1">
                You'll see notifications for @mentions, validation failures, issues, and schema changes here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-secondary-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    px-4 py-3 border-l-4 cursor-pointer transition-colors duration-150
                    ${getNotificationColor(notification.type, notification.severity)}
                    ${!notification.read ? 'hover:bg-opacity-75' : 'opacity-75 hover:opacity-90'}
                  `}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type, notification.severity)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className={`text-sm font-medium text-secondary-900 ${!notification.read ? 'font-semibold' : ''}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-secondary-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center mt-2 space-x-2">
                            <ClockIcon className="h-3 w-3 text-secondary-400" />
                            <span className="text-xs text-secondary-500">
                              {formatNotificationTime(notification.created_at)}
                            </span>
                            {notification.severity && (
                              <span className={`
                                inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium
                                ${notification.severity === 'high' ? 'bg-danger-100 text-danger-800' : ''}
                                ${notification.severity === 'medium' ? 'bg-warning-100 text-warning-800' : ''}
                                ${notification.severity === 'low' ? 'bg-primary-100 text-primary-800' : ''}
                              `}>
                                {notification.severity}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {!notification.read && (
                            <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                          )}
                          <ChevronRightIcon className="h-4 w-4 text-secondary-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-secondary-200 bg-secondary-50">
            <button
              onClick={() => {
                navigate('/notifications');
                onClose();
              }}
              className="text-xs text-primary-600 hover:text-primary-800 w-full text-center"
            >
              View all notifications
            </button>
          </div>
        )}
      </div>
    </Transition>
  );
};

export default NotificationCenter;